import { Link } from "react-router-dom";
import { GAME } from "@/constants/testIds";
import { Play, Trophy, Calendar, Zap, Shield, Coins, Award, Flame } from "lucide-react";
import { todayString } from "@/lib/gameEngine";
import { getStreak, getUnlockedMap } from "@/lib/achievements";
import { ACHIEVEMENTS } from "@/lib/achievements";

const ALLEY_BG =
  "https://images.unsplash.com/photo-1639164311554-f42f14d3a570?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYnJpY2slMjBhbGxleXdheSUyMG5pZ2h0fGVufDB8fHx8MTc4MDEyNDc0OXww&ixlib=rb-4.1.0&q=85";

export default function TitleScreen() {
  const today = todayString();
  const streak = getStreak(today);
  const unlockedCount = Object.keys(getUnlockedMap()).length;
  const totalAchievements = ACHIEVEMENTS.length;
  return (
    <div
      data-testid={GAME.titleScreen}
      className="relative min-h-screen w-full overflow-hidden scanlines vignette"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url(${ALLEY_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-10 py-5 font-hud text-xs sm:text-sm text-[var(--crt-green)]/80">
        <span className="tracking-[0.3em]">// BLACK EMIGO MUSIC</span>
        <div className="flex items-center gap-3">
          {streak > 0 && (
            <span
              data-testid="title-streak-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--neon-red)]/60 bg-black/60 text-[var(--neon-red)]"
              title={`Daily streak — ${streak} day${streak === 1 ? "" : "s"}`}
            >
              <Flame className="w-3.5 h-3.5" />
              <span className="tracking-wider">STREAK x{streak}</span>
            </span>
          )}
          <span className="tracking-[0.3em]">2026 · ST.AL EDITION</span>
        </div>
      </header>

      <main className="relative z-10 px-5 sm:px-10 pb-44 pt-2 sm:pt-6 max-w-6xl mx-auto">
        <p className="font-hud text-center text-[var(--street-yellow)] tracking-[0.4em] text-xs sm:text-sm mb-6 flicker">
          A 2D STREET PLATFORMER
        </p>

        <div className="text-center select-none">
          <h1 className="font-display title-glitch leading-[0.86] text-[16vw] sm:text-[12vw] md:text-[10rem]">
            ADVENTURES
          </h1>
          <h1 className="font-display title-glitch leading-[0.86] text-[14vw] sm:text-[10vw] md:text-[9rem]">
            OF BLACK
          </h1>
          <h1
            className="font-display title-glitch-red leading-[0.86] text-[20vw] sm:text-[15vw] md:text-[13rem] tracking-tight"
            style={{ WebkitTextStroke: "1px rgba(0,0,0,0.7)" }}
          >
            EMIGO
          </h1>

          <div className="relative inline-block mt-4">
            <span className="font-marker text-[var(--street-yellow)] text-3xl sm:text-4xl rotate-[-4deg] inline-block drop-shadow-[0_2px_0_rgba(0,0,0,0.8)]">
              run the block
            </span>
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center">
          <Link
            to="/play"
            data-testid={GAME.startBtn}
            className="btn-neon group inline-flex items-center gap-3 bg-[var(--neon-red)] hover:bg-red-600 text-white font-body font-bold uppercase tracking-[0.18em] text-base sm:text-lg px-8 py-4 rounded-md transition-all hover:scale-[1.03]"
          >
            <Play className="w-5 h-5 fill-white" />
            Press Start
          </Link>
          <Link
            to="/play?mode=daily"
            data-testid="daily-run-button"
            className="inline-flex items-center gap-3 bg-[var(--street-yellow)] hover:bg-yellow-300 text-black font-body font-bold uppercase tracking-[0.18em] text-base sm:text-lg px-8 py-4 rounded-md transition-all hover:scale-[1.03]"
            title="Same level for everyone today"
          >
            <Calendar className="w-5 h-5" />
            Daily Run · {today.slice(5)}
          </Link>
          <Link
            to="/leaderboard"
            data-testid={GAME.leaderboardBtn}
            className="inline-flex items-center gap-3 bg-transparent hover:bg-white/5 border-2 border-white/40 hover:border-white text-white font-body font-bold uppercase tracking-[0.18em] text-base sm:text-lg px-8 py-4 rounded-md transition-all"
          >
            <Trophy className="w-5 h-5" />
            Leaderboard
          </Link>
          <Link
            to="/achievements"
            data-testid="achievements-button"
            className="inline-flex items-center gap-3 bg-transparent hover:bg-white/5 border-2 border-[var(--crt-green)]/60 hover:border-[var(--crt-green)] text-[var(--crt-green)] font-body font-bold uppercase tracking-[0.18em] text-base sm:text-lg px-8 py-4 rounded-md transition-all whitespace-nowrap"
          >
            <Award className="w-5 h-5" />
            Badges&nbsp;{unlockedCount}/{totalAchievements}
          </Link>
        </div>

        <p className="mt-10 font-hud text-center text-white/60 text-base sm:text-lg max-w-md mx-auto">
          SURVIVE THE SNAKES. OUTRUN THE POLICE.
          <br />
          STOMP THE THUGS. SLAY THE BOSSES.
        </p>

        {/* Enemy preview */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
          {[
            { name: "ARMY", color: "#4a5d3a" },
            { name: "SNAKES", color: "#39ff14" },
            { name: "POLICE", color: "#1a3b8e" },
            { name: "THUGS", color: "#7a1f1f" },
          ].map((e) => (
            <div key={e.name} className="border border-white/15 bg-black/40 px-3 py-2 text-center">
              <div className="w-full h-3 mb-2" style={{ background: e.color }} />
              <p className="font-hud text-[var(--crt-green)] text-base tracking-wider">{e.name}</p>
            </div>
          ))}
        </div>

        {/* Power-ups + Boss callout */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
          <div className="border border-[var(--street-yellow)]/40 bg-black/50 p-4 rounded-md">
            <p className="font-hud text-[var(--street-yellow)] text-base tracking-[0.25em] mb-2">// POWER-UPS</p>
            <ul className="space-y-1.5 font-hud text-white/85 text-base">
              <li className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#ffcc00]" /> CHAIN — DOUBLE JUMP
              </li>
              <li className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#39ff14]" /> SHADES — 5s INVINCIBLE
              </li>
              <li className="flex items-center gap-2">
                <Coins className="w-4 h-4 text-[#ff003c]" /> RED CUP — 10s x2 COINS
              </li>
            </ul>
          </div>
          <div className="border border-[var(--neon-red)]/40 bg-black/50 p-4 rounded-md">
            <p className="font-hud text-[var(--neon-red)] text-base tracking-[0.25em] mb-2">// BOSS RUSH</p>
            <ul className="space-y-1.5 font-hud text-white/85 text-base">
              <li>EVERY 5 BLOCKS: SWAT VAN ↔ CHOPPER</li>
              <li>4 HP · STOMP TO BREAK</li>
              <li>DEFEAT = BIG SCORE + POWER-UP DROP</li>
            </ul>
          </div>
        </div>
      </main>
    </div>
  );
}
